import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { KeycloakToken } from "@youm-front/keycloak/domain";
import Keycloak from "keycloak-js";
import { Observable } from "rxjs";
import { filter, map, mapTo, mergeMap, shareReplay } from "rxjs/operators";
import { KEYCLOAK_CONFIG, KeycloakConfig } from "../../providers";
import { KeycloakService } from "./keycloak.service";

@Injectable()
export class KeycloakImplService implements KeycloakService {
  private readonly keycloak: Keycloak;

  init$ = new Observable<null | KeycloakToken>((subscriber) => {
    // Use 'shareReplay' to sync simultaneous requests
    this.init()
      .then((_) => {
        subscriber.next(_);
        subscriber.complete();
      })
      .catch((_) => subscriber.error(_));
  }).pipe(
    // A 'Keycloak' instance can only be initialized once.
    shareReplay(1)
  );

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(KEYCLOAK_CONFIG)
    private readonly keycloakConfig: KeycloakConfig,
    private readonly route: ActivatedRoute
  ) {
    this.keycloak = new Keycloak({
      url: this.keycloakConfig.url,
      realm: this.keycloakConfig.realm,
      clientId: this.keycloakConfig.clientId
    });
  }

  get onSuccessRedirectToRedirectUri$() {
    return this.route.fragment.pipe(
      map((_) => new URLSearchParams(_ || "")),
      map((_) => _.get("session_state")),
      filter((_): _ is string => !!_),
      mergeMap((_) => this.init$.pipe(mapTo(_))),
      filter((_) => _ === this.keycloak.tokenParsed?.session_state),
      mapTo(void 0)
    );
  }

  get token() {
    return this.keycloak.token as KeycloakToken;
  }

  async signOut() {
    return this.keycloak.logout();
  }

  async login() {
    await this.keycloak.login({
      redirectUri: this.document.location.href
    });
  }

  private async init(): Promise<null | KeycloakToken> {
    const authenticated = await this.keycloak.init({
      // Use 'check-sso' for silent check to avoid redirection
      // https://www.keycloak.org/docs/latest/securing_apps/#using-the-adapter
      onLoad: "check-sso",
      silentCheckSsoRedirectUri:
        window.location.origin + "/assets/silent-check-sso.html"
    });

    const { token } = this.keycloak;
    if (authenticated && !token) {
      throw new Error("Authenticated with empty token");
    } else if (authenticated && token) {
      return token as KeycloakToken;
    }

    return null;
  }
}
